import { replace } from "lodash";
import React from "react";
import { axios } from "@/services/axios";
import PropTypes from "prop-types";
import Switch from "antd/lib/switch";
import Modal from "antd/lib/modal";
import Form from "antd/lib/form";
import Alert from "antd/lib/alert";
import Button from "antd/lib/button";
import Input from "antd/lib/input";
import notification from "@/services/notification";
import { wrap as wrapDialog, DialogPropType } from "@/components/DialogWrapper";
import InputWithCopy from "@/components/InputWithCopy";
import HelpTrigger from "@/components/HelpTrigger";

const API_SHARE_URL = "api/dashboards/{id}/share";

class ShareDashboardDialog extends React.Component {
  static propTypes = {
    dashboard: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
    hasOnlySafeQueries: PropTypes.bool.isRequired,
    dialog: DialogPropType.isRequired,
  };

  formItemProps = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
    style: { marginBottom: 7 },
  };

  constructor(props) {
    super(props);
    const { dashboard } = this.props;

    this.state = {
      saving: false,
      authSaving: false,
      publicAuthEnabled: Boolean(dashboard?.options?.public_access_auth?.enabled),
      publicAuthUsername: dashboard?.options?.public_access_auth?.username || "",
      publicAuthPassword: "",
    };

    this.apiUrl = replace(API_SHARE_URL, "{id}", dashboard.id);
    this.enabled = this.props.hasOnlySafeQueries || dashboard.publicAccessEnabled;
  }

  static get headerContent() {
    return (
      <React.Fragment>
        Share Dashboard
        <div className="modal-header-desc">
          Allow public access to this dashboard with a secret address. <HelpTrigger type="SHARE_DASHBOARD" />
        </div>
      </React.Fragment>
    );
  }

  enableAccess = () => {
    const { dashboard } = this.props;
    this.setState({ saving: true });

    axios
      .post(this.apiUrl)
      .then(data => {
        dashboard.publicAccessEnabled = true;
        dashboard.public_url = data.public_url;
      })
      .catch(() => {
        notification.error("Failed to turn on sharing for this dashboard");
      })
      .finally(() => {
        this.setState({ saving: false });
      });
  };

  disableAccess = () => {
    const { dashboard } = this.props;
    this.setState({ saving: true });

    axios
      .delete(this.apiUrl)
      .then(() => {
        dashboard.publicAccessEnabled = false;
        delete dashboard.public_url;
      })
      .catch(() => {
        notification.error("Failed to turn off sharing for this dashboard");
      })
      .finally(() => {
        this.setState({ saving: false });
      });
  };

  onChange = checked => {
    if (checked) {
      this.enableAccess();
    } else {
      this.disableAccess();
    }
  };

  savePublicAccessAuth = () => {
    const { dashboard } = this.props;
    const { publicAuthEnabled, publicAuthUsername, publicAuthPassword } = this.state;
    const username = (publicAuthUsername || "").trim();
    if (publicAuthEnabled && !username) {
      notification.error("Please provide a username for public dashboard authentication.");
      return;
    }

    // Server stores only a password hash; the UI never receives it. If protection was never
    // saved successfully before, a password is required or `enabled` stays false in DB / no hash.
    const hadSavedPublicCredentials = Boolean((dashboard?.options?.public_access_auth?.username || "").trim());
    if (publicAuthEnabled && !hadSavedPublicCredentials && !(publicAuthPassword || "").trim()) {
      notification.error(
        "Enter a password for the public link. You only need to fill this once when enabling protection."
      );
      return;
    }

    const publicAccessAuth = {
      enabled: publicAuthEnabled,
      username,
    };
    if (publicAuthPassword) {
      publicAccessAuth.password = publicAuthPassword;
    }

    const payload = {
      name: dashboard.name,
      layout: dashboard.layout,
      version: dashboard.version,
      tags: dashboard.tags,
      is_draft: dashboard.is_draft,
      is_archived: dashboard.is_archived,
      dashboard_filters_enabled: dashboard.dashboard_filters_enabled,
      options: {
        ...(dashboard.options || {}),
        public_access_auth: publicAccessAuth,
      },
    };

    this.setState({ authSaving: true });
    axios
      .post(`api/dashboards/${dashboard.id}`, payload)
      .then(updatedDashboard => {
        dashboard.options = updatedDashboard.options;
        dashboard.version = updatedDashboard.version;
        this.setState({
          publicAuthPassword: "",
          publicAuthEnabled: Boolean(updatedDashboard?.options?.public_access_auth?.enabled),
          publicAuthUsername: updatedDashboard?.options?.public_access_auth?.username || "",
        });
        notification.success("Public dashboard login settings saved.");
      })
      .catch(() => {
        notification.error("Failed to save public dashboard login settings.");
      })
      .finally(() => this.setState({ authSaving: false }));
  };

  render() {
    const { dialog, dashboard, hasOnlySafeQueries } = this.props;
    const headerContent = this.constructor.headerContent;
    return (
      <Modal {...dialog.props} title={headerContent} footer={null}>
        <Form layout="horizontal">
          {!hasOnlySafeQueries && (
            <Form.Item>
              <Alert
                message="For your security, sharing is currently not supported for dashboards containing queries with text parameters. Consider changing the text parameters in your query to a different type."
                type="error"
              />
            </Form.Item>
          )}

          <Form.Item label="Allow public access" {...this.formItemProps}>
            <Switch
              checked={dashboard.publicAccessEnabled}
              onChange={this.onChange}
              loading={this.state.saving}
              disabled={!this.enabled}
              data-test="PublicAccessEnabled"
            />
          </Form.Item>
          {dashboard.public_url && (
            <Form.Item label="Secret address" {...this.formItemProps}>
              <InputWithCopy value={dashboard.public_url} data-test="SecretAddress" />
            </Form.Item>
          )}
          {dashboard.public_url && (
            <React.Fragment>
              <Form.Item label="Protect public link" {...this.formItemProps}>
                <Switch
                  checked={this.state.publicAuthEnabled}
                  onChange={checked => this.setState({ publicAuthEnabled: checked })}
                />
              </Form.Item>
              <Form.Item label="Public username" {...this.formItemProps}>
                <Input
                  value={this.state.publicAuthUsername}
                  onChange={event => this.setState({ publicAuthUsername: event.target.value })}
                  disabled={!this.state.publicAuthEnabled}
                />
              </Form.Item>
              <Form.Item label="Public password" {...this.formItemProps}>
                <Input.Password
                  value={this.state.publicAuthPassword}
                  onChange={event => this.setState({ publicAuthPassword: event.target.value })}
                  disabled={!this.state.publicAuthEnabled}
                  placeholder="Leave empty to keep existing password"
                />
              </Form.Item>
              <Form.Item label=" " colon={false} {...this.formItemProps}>
                <Button loading={this.state.authSaving} onClick={this.savePublicAccessAuth}>
                  Save public login settings
                </Button>
              </Form.Item>
            </React.Fragment>
          )}
        </Form>
      </Modal>
    );
  }
}

export default wrapDialog(ShareDashboardDialog);
